import { useLoad } from "@allergy123/api-client/react";
import { Card, Progress, Status, Table, Td, Th } from "@allergy123/ui";
import { Link, useParams } from "react-router";

/** One learner: assignments, each enrollment's progress, and the five records in full. */
export function LearnerPage() {
  const { id = "" } = useParams();
  const data = useLoad(async (a) => {
    const [cat, detail] = await Promise.all([a.catalog(), a.admin.learner(id)]);
    return { cat, detail };
  }, [id]);
  if (data.state === "error") return <Status kind="error" label={`Couldn't load this learner (${data.error?.message})`} />;
  if (!data.data) return <p className="text-ink-secondary">Loading…</p>;
  const { cat, detail } = data.data;
  const { learner, learning, records } = detail;
  const fmt = (iso: string) => new Date(iso).toLocaleString();

  return (
    <>
      <p className="mb-2 text-small text-ink-secondary">
        <Link to="/learners">Learners</Link>
      </p>
      <h1 className="mb-1 text-h1 font-semibold text-anchor">{learner.name ?? learner.email}</h1>
      <p className="mb-8 text-ink-secondary">
        {learner.email} · {learner.organizations.length ? learner.organizations.map((o) => `${o.name} (${o.role})`).join(", ") : "individual learner"} · {learner.status}
      </p>

      <section className="mb-10">
        <h2 className="mb-3 text-h2 font-semibold text-anchor">Assignments</h2>
        {learning.assignments.length === 0 ? (
          <p className="text-ink-secondary">No pathway assigned.</p>
        ) : (
          <ul className="grid gap-2">
            {learning.assignments.map((a) => (
              <li key={a.id}>
                {cat.pathways.find((p) => p.id === a.pathwayId)?.title ?? a.pathwayId} <span className="text-small text-ink-secondary">· {a.basis} · {fmt(a.createdAt)}</span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="mb-10">
        <h2 className="mb-3 text-h2 font-semibold text-anchor">Modules</h2>
        <div className="grid gap-4 md:grid-cols-2">
          {learning.enrollments.map((e) => {
            const m = cat.modules.find((x) => x.code === e.moduleCode);
            return (
              <Card key={e.id} dense>
                <p className="text-small font-medium text-ink-secondary">
                  {e.moduleCode} · release {e.releaseVersion} · {e.status}
                </p>
                <h3 className="mb-3 font-semibold text-anchor">{m?.title ?? e.moduleCode}</h3>
                <Progress value={e.completions.length} max={m?.classes.length ?? 0} label="Classes complete" />
              </Card>
            );
          })}
          {learning.enrollments.length === 0 && <p className="text-ink-secondary">Not enrolled in any module.</p>}
        </div>
      </section>

      <div className="grid gap-6">
        <Card as="section">
          <h2 className="mb-2 text-h3 font-semibold text-anchor">Content completion ({records.completions.length})</h2>
          <Table>
            <thead>
              <tr>
                <Th>Class</Th>
                <Th>Module</Th>
                <Th>Release</Th>
                <Th>Completed</Th>
              </tr>
            </thead>
            <tbody>
              {records.completions.map((c) => (
                <tr key={c.classCode + c.completedAt}>
                  <Td>{c.classCode}</Td>
                  <Td>{c.moduleCode}</Td>
                  <Td>{c.releaseVersion}</Td>
                  <Td>{fmt(c.completedAt)}</Td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card>
        <Card as="section">
          <h2 className="mb-2 text-h3 font-semibold text-anchor">Knowledge results ({records.knowledgeResults.length})</h2>
          <Table>
            <thead>
              <tr>
                <Th>Check</Th>
                <Th>Attempt</Th>
                <Th>Score</Th>
                <Th>Critical items</Th>
                <Th>Passed</Th>
                <Th>Recorded</Th>
              </tr>
            </thead>
            <tbody>
              {records.knowledgeResults.map((k) => (
                <tr key={k.checkCode + k.attempt}>
                  <Td>{k.checkCode}</Td>
                  <Td>{k.attempt}</Td>
                  <Td>{k.scorePercent}%</Td>
                  <Td>{k.criticalItemsPassed ? "All right" : "Missed"}</Td>
                  <Td>{k.passed ? "Yes" : "No"}</Td>
                  <Td>{fmt(k.recordedAt)}</Td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card>
        <Card as="section">
          <h2 className="mb-2 text-h3 font-semibold text-anchor">Practical evidence ({records.practicalEvidence.length})</h2>
          <p className="text-small text-ink-secondary">{records.practicalEvidence.length === 0 ? "None recorded." : "Structured assessor records; no patient information."}</p>
        </Card>
        <Card as="section">
          <h2 className="mb-2 text-h3 font-semibold text-anchor">Recognition eligibility ({records.eligibility.length})</h2>
          <p className="text-small text-ink-secondary">{records.eligibility.length === 0 ? "No evaluation yet." : ""}</p>
        </Card>
        <Card as="section">
          <h2 className="mb-2 text-h3 font-semibold text-anchor">Recognitions issued ({records.recognitions.length})</h2>
          <p className="text-small text-ink-secondary">{records.recognitions.length === 0 ? "None issued." : ""}</p>
        </Card>
      </div>
    </>
  );
}
